import { useEffect, useRef, useState } from 'react';
import { IconClipboard, IconEyeOff } from './icons';
import { type FeedStory } from '../repositories/readerStory';
import { type HideReason } from '../repositories/readerFeedbackRepository';
import { readerDomainLabel } from '../utils/storyLinks';
import { formatStoryTitle } from '../utils/storyTitle';
import { storyPermalinkUrl } from '../utils/storyPermalink';
import { CopyLinkControl } from './CopyLinkControl';

export function StoryActions({ article, expanded, onHide, onExpand }: {
  article: FeedStory; expanded: boolean; onHide: () => void; onExpand: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const dismiss = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);
  return <span className="beta-story-side" ref={root} onClick={e => { e.stopPropagation(); e.preventDefault(); }} onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); e.stopPropagation(); } }}>
    <button className="reader-hide-control" type="button" title="Not interested (x)" aria-label="Not interested (x)" onClick={onHide}><IconEyeOff size={16} /></button>
    <button className="reader-expand-control" type="button" aria-label={expanded ? 'Collapse story' : 'Expand story'} aria-expanded={expanded} onClick={onExpand}><span className="beta-chevron" /></button>
    <button className="reader-row-menu" type="button" aria-label={`Story menu: ${formatStoryTitle(article.friendly_title || article.title)}`} aria-expanded={open} onClick={() => setOpen(value => !value)}>⋯</button>
    {open && <span className="reader-popover reader-story-popover" role="group" aria-label="Story actions">
      <button type="button" onClick={() => { setOpen(false); onHide(); }}><IconEyeOff size={14} />Not interested</button>
      <CopyLinkControl getUrl={() => storyPermalinkUrl(article)} title="Copy link" ariaLabel="Copy link" copiedMessage="Link copied" errorMessage="Could not copy link"><IconClipboard size={14} />Copy link</CopyLinkControl>
    </span>}
  </span>;
}

export function HiddenStoryRow({ article, prompt, busy, query, onReason, onUnhide }: {
  article: FeedStory; prompt: boolean; busy: boolean; query?: string;
  onReason: (reason: HideReason, label: string, properties?: Record<string, unknown>, text?: string) => void;
  onUnhide: () => void;
}) {
  const [other, setOther] = useState(false);
  const [text, setText] = useState('');
  const reasons: { reason: HideReason; label: string; properties?: Record<string, unknown> }[] = [
    ...(query ? [{ reason: 'not_about_feed' as const, label: `Not about “${query}”`, properties: { query } }] :
      (article.for_you_searches || []).map(feed => ({ reason: 'not_about_feed' as const, label: `Not about ${feed.name}`, properties: { feed_id: feed.id } }))),
    { reason: 'less_from_domain', label: `Less from ${readerDomainLabel(article)}` },
    { reason: 'low_quality', label: 'Low quality' }, { reason: 'already_seen', label: 'Already seen' },
  ];
  return <div className="reader-hidden-story" data-hidden-story={article.id}>
    <div className="reader-hidden-line"><span className="reader-hidden-desktop">{formatStoryTitle(article.friendly_title || article.title)}</span><span className="reader-hidden-mobile">{formatStoryTitle(article.friendly_title || article.title)}</span>
      <button type="button" disabled={busy} onClick={onUnhide}>Unhide</button></div>
    {prompt && <div className="reader-hide-prompt"><p>What was wrong with it?<span className="reader-hide-help"> This helps us tune your feed.</span></p>
      {other ? <form onSubmit={event => { event.preventDefault(); onReason('other', text.trim() ? `Other: ${text.trim()}` : 'Other', {}, text.trim()); }}>
        <label><span className="reader-sr">What was wrong with this story?</span>
        <input autoFocus placeholder="What was wrong with this story?" value={text} maxLength={2000} onChange={event => setText(event.target.value)} disabled={busy} /></label>
        <button type="submit" disabled={busy}>Send</button></form> : <div className="reader-reason-buttons">
          {reasons.map(item => <button type="button" key={item.label} disabled={busy} onClick={() => onReason(item.reason, item.label, item.properties)}>{item.label}</button>)}
          <button type="button" disabled={busy} onClick={() => setOther(true)}>Other…</button>
        </div>}
    </div>}
  </div>;
}
